/**
 * PublicRoute — for pages that authenticated users shouldn't see
 * (e.g., /login, /otp). Redirects logged-in users away.
 *
 * Where they get sent depends on whether their profile exists:
 *   - profile exists  → /welcome
 *   - profile missing → /signup  (they finished OTP but never completed signup)
 *
 * Why we check `user`, not just `isAuthenticated`:
 *
 *   After a successful OTP, OtpPage runs (in this order):
 *     1. dispatch(loginSuccess({ accessToken, user }))   // synchronous
 *     2. navigate('/signup' or '/welcome', { replace: true })
 *
 *   The Redux dispatch triggers an immediate re-render of the CURRENT
 *   route (still `/otp`) before the navigate() call has a chance to
 *   change the URL. On that re-render, this guard sees the new
 *   `isAuthenticated=true` and returns a <Navigate> — which is a
 *   declarative redirect that the router applies before the imperative
 *   `navigate('/signup')` from OtpPage takes effect. So whatever target
 *   THIS guard picks WINS the race.
 *
 *   Before this fix, we always returned <Navigate to="/dashboard">,
 *   which silently overrode OtpPage's `/signup` redirect for new users
 *   (Session 4 bug: new users landed on /dashboard).
 *
 *   Now we mirror OtpPage's own logic: if `user` is null (the OTP
 *   handler sets it to null for new users), the right destination is
 *   /signup, and both code paths agree.
 *
 * Bonus side-effect: this also enforces the rule "you can't skip the
 * signup wizard by navigating to /login when half-authenticated", which
 * is the same invariant ProtectedRoute now enforces.
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

/** @param {{ children: React.ReactNode }} props */
export function PublicRoute({ children }) {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    // A clinic-sent face-capture link takes precedence: once the user is
    // authenticated (e.g. right after OTP), send them straight to capture.
    // This also neutralises the race with OtpPage's own navigate — whichever
    // fires first, both target /face-capture.
    let invite = null;
    try { invite = sessionStorage.getItem('pendingCaptureInvite'); } catch { invite = null; }
    if (user && invite) {
      return <Navigate to={`/face-capture?invite=${encodeURIComponent(invite)}`} replace />;
    }
    // A support-sent re-run link, remembered at boot (or by ProtectedRoute
    // before it bounced the client here). Left in place: RerunPayPage clears
    // it on arrival, so this guard and OtpPage cannot disagree mid-flip.
    let rerunId = null;
    try { rerunId = sessionStorage.getItem('pendingRerunId'); } catch { rerunId = null; }
    if (user && rerunId) {
      return <Navigate to={`/rerun/${encodeURIComponent(rerunId)}`} replace />;
    }
    return <Navigate to={user ? '/dashboard' : '/signup'} replace />;
  }

  return children;
}

export default PublicRoute;
